import { useRouter } from 'vue-router';
import { Message, Modal } from '@arco-design/web-vue';
import { ref } from 'vue';

import { useUserStore } from '@/store';

export default function useUser() {
  const router = useRouter();
  const userStore = useUserStore();
  const isLoggingOut = ref(false);

  const logout = async (logoutTo?: string, showConfirm = true) => {
    // 如果正在退出中，直接返回
    if (isLoggingOut.value) {
      return;
    }

    // 显示确认对话框
    if (showConfirm) {
      try {
        await new Promise((resolve, reject) => {
          Modal.confirm({
            title: '确认退出',
            content: '您确定要退出登录吗？',
            okText: '确定',
            cancelText: '取消',
            onOk: resolve,
            onCancel: reject,
          });
        });
      } catch {
        // 用户取消退出
        return;
      }
    }

    try {
      isLoggingOut.value = true;

      // 显示加载提示
      const loadingMessage = Message.loading({
        content: '正在退出登录...',
        duration: 0,
      });

      // 直接使用强制退出，不向服务器发送请求
      userStore.forceLogout();

      // 关闭加载提示
      loadingMessage.close();

      // 显示成功消息
      Message.success('退出登录成功');

      // 获取当前路由信息
      const currentRoute = router.currentRoute.value;

      // 跳转到指定页面或登录页
      const targetRoute =
        logoutTo && typeof logoutTo === 'string' ? logoutTo : 'login';

      // 清除当前路由的查询参数，只保留重定向信息
      const query: Record<string, string> = {};
      if (currentRoute.name && currentRoute.name !== 'login') {
        query.redirect = currentRoute.name as string;
      }

      // 跳转路由
      await router.push({
        name: targetRoute,
        query,
        replace: true, // 使用replace避免浏览器后退按钮问题
      });
    } catch (error) {
      console.error('Logout failed:', error);
      Message.error('退出登录失败，请重试');
    } finally {
      isLoggingOut.value = false;
    }
  };

  // 强制退出（不显示确认对话框）
  const forceLogout = async (logoutTo?: string) => {
    return logout(logoutTo, false);
  };

  return {
    logout,
    forceLogout,
    isLoggingOut,
  };
}
